/**
 * Get auth from EaseVerse browser extension, then exchange for a reader-slot token.
 * The extension's token lives in the "ext" slot; Reader needs its own "reader" slot token
 * so the two never kick each other out.
 *
 * Canonical pattern: EaseVerse Lens frontend src/background.ts (EASEVERSE_GET_AUTH handler).
 * Keep in sync across: Collections, Reader, Account.
 * Reader variant: includes /auth/exchange-token for reader-slot tokens.
 */

import { API_BASE as AUTH_API_BASE, EXTENSION_IDS } from './config.js'
const DEVICE_ID_KEY = 'easeverse_device_id'

function getOrCreateDeviceId() {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY)
  if (!deviceId) {
    deviceId = typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c => (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16))
    localStorage.setItem(DEVICE_ID_KEY, deviceId)
  }
  return deviceId
}

function getAuthFromExtensionOne(extensionId, timeoutMs = 2000) {
  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(null), timeoutMs)
    if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
      clearTimeout(timeout)
      resolve(null)
      return
    }
    chrome.runtime.sendMessage(
      extensionId,
      { type: 'EASEVERSE_GET_AUTH' },
      (response) => {
        clearTimeout(timeout)
        if (chrome.runtime.lastError) {
          resolve(null)
          return
        }
        if (response?.success && response.token) {
          resolve({
            token: response.token,
            deviceId: response.deviceId || '',
            email: response.email || '',
            displayName: response.displayName || '',
            locale: response.locale || '',
          })
        } else {
          resolve(null)
        }
      }
    )
  })
}

export async function getExtensionToken() {
  if (!EXTENSION_IDS.length) return null
  try {
    for (const extensionId of EXTENSION_IDS) {
      const result = await getAuthFromExtensionOne(extensionId)
      if (result) return result
    }
    await new Promise((r) => setTimeout(r, 400))
    for (const extensionId of EXTENSION_IDS) {
      const result = await getAuthFromExtensionOne(extensionId)
      if (result) return result
    }
    return null
  } catch {
    return null
  }
}

/**
 * Lightweight check: is the extension installed and logged in?
 * Returns true/false without performing any token exchange.
 */
export async function checkExtensionAvailable() {
  const extAuth = await getExtensionToken()
  return !!extAuth
}

/**
 * Try to get a reader-slot token by exchanging the extension's token.
 * Returns { token, user_id } on success, null otherwise.
 */
export async function getReaderTokenFromExtension() {
  const extAuth = await getExtensionToken()
  if (!extAuth) return null
  return exchangeExtensionToken(extAuth.token)
}

/**
 * Exchange an extension token for a reader token. Exported for tests.
 * Returns { token, user_id } on success, { deviceLimit, devices, maxDevices,
 * loginTicket } when the reader device slot is full (409 — the caller shows
 * the same eviction dialog as OTP/Google login), or null on any other failure.
 */
export async function exchangeExtensionToken(extToken) {
  try {
    const response = await fetch(`${AUTH_API_BASE}/auth/exchange-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${extToken}`
      },
      body: JSON.stringify({ client_type: 'reader', device_id: getOrCreateDeviceId() })
    })

    if (response.status === 409) {
      const data = await response.json().catch(() => null)
      if (data?.code === 'DEVICE_LIMIT_REACHED') {
        return {
          deviceLimit: true,
          devices: data.devices || [],
          maxDevices: data.max_devices || 0,
          loginTicket: data.login_ticket,
        }
      }
      return null
    }
    if (!response.ok) return null

    const data = await response.json()
    return data.token ? { token: data.token, user_id: data.user_id } : null
  } catch {
    return null
  }
}
